import { MAX_JOB_EQUIPMENT_LINKS } from '@/lib/jobs/jobEquipmentConstraints';
import type { Booking } from '@/types/booking';
import type { CreateJobDto } from '@/types/job';

function bookingContactName(booking: Booking) {
  if (!booking.contact) return null;
  return `${booking.contact.firstName}${booking.contact.lastName ? ' ' + booking.contact.lastName : ''}`;
}

/**
 * The booking-detail "Create job" adapter: builds the service-call job DTO
 * from a service call (booking). Mirrors the server-side
 * ServiceCallProvisioningService.buildCreateJobDto shape, including the exact
 * saved-address identity (`serviceAddress.addressId`) when the booking was
 * placed at one of the customer's configured addresses — without it the job
 * falls back to fuzzy street matching for equipment/location resolution, and
 * the equipment captured at intake (`equipmentIds`), which the server path
 * carries onto the job the same way.
 */
export function buildJobFromBookingDto(
  booking: Booking,
  contactIdOverride?: string,
): CreateJobDto | null {
  const contactId = contactIdOverride || booking.contactId || booking.contact?.id;
  if (!contactId) return null;

  const serviceName =
    booking.jobType?.name ?? booking.serviceName ?? booking.title ?? 'Service call';
  const contactName = bookingContactName(booking);
  const address = booking.address;
  const serviceAddress =
    address?.street && address.city && address.state && address.zip
      ? {
          // Keep the exact saved-address identity when the booking has one, so
          // the job doesn't fall back to fuzzy street matching for
          // location-scoped features.
          addressId: booking.serviceAddressId ?? undefined,
          street: address.street,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country || undefined,
        }
      : undefined;
  // Provenance travels structurally on `bookingId`; the note is prose for the
  // office, so it carries no identifier rather than a sliced UUID. Wording
  // matches the server path (ServiceCallProvisioningService).
  const internalNotes = ['Created from service call.', booking.internalNotes ?? null].filter(
    Boolean,
  );
  // Equipment named at intake travels onto the job. The server DTO declares
  // `@ArrayUnique` + `@ArrayMaxSize(20)`, so dedupe and cap here rather than
  // letting a duplicated or over-long selection come back as a raw 400.
  // Ownership is re-validated in JobsService.create (non-matching units are
  // dropped, never an error).
  const equipmentIds = Array.from(new Set(booking.equipmentIds ?? [])).slice(
    0,
    MAX_JOB_EQUIPMENT_LINKS,
  );

  return {
    name: contactName ? `${contactName} - ${serviceName}` : serviceName,
    contactId,
    jobType: booking.jobType?.name ?? serviceName,
    jobTypeId: booking.jobType?.id,
    priority: 'NORMAL',
    bookingType: 'ONE_OFF',
    // SPEC-service-call-lifecycle D1/D2 — the client never sends an
    // isServiceCall flag: the field is not on the server DTO (forbidNonWhitelisted
    // would 400 on it), and membership derives from the resolved job type. A
    // job created from a legacy jobless booking is a service call only if its
    // job type says so — matching the attach-to-job semantics ("no longer
    // appears in Service calls"). Provenance is MANUAL: an office user clicked
    // "Create job" (the FE Booking shape carries no original booking source to
    // map; the server-side heal path does that mapping itself).
    source: 'MANUAL',
    bookingId: booking.id,
    scheduledStart: booking.scheduledStart,
    scheduledEnd: booking.scheduledEnd,
    assignedTo: booking.technicians?.[0]?.id,
    notes: booking.title && booking.title !== serviceName ? booking.title : undefined,
    internalNotes: internalNotes.length > 0 ? internalNotes.join('\n\n') : undefined,
    serviceAddress,
    equipmentIds: equipmentIds.length > 0 ? equipmentIds : undefined,
  };
}
